import {
  Alert,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { CircleMinus, CircleX, EllipsisVertical, Settings } from "lucide-react";

import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { NameEmailAvatarRound } from "components/shared/shared";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Role, UserMembership } from "data/models";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generatePath, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { deleteOrganisationMember } from "data/mutations";
import { resolveIdentityId } from "utils/identity";

type Props = {
  memberships: UserMembership[];
  isLoading: boolean;
};

const emptyInvitation: UserMembership = {
  organisationId: "",
  userMemberId: "",
  status: "",
  organisationName: "",
  userFullName: "",
  userEmail: "",
  organisationEmail: "",
  userFirstName: "",
  userLastName: "",
};

export const InvitationList = ({ memberships, isLoading }: Props) => {
  const [modal, setModal] = useState<{
    isOpen: boolean;
    invitation: UserMembership;
  }>({
    isOpen: false,
    invitation: emptyInvitation,
  });
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const user = GetUserIfLoggedIn(identity);
  const history = useHistory();

  const handleClickOpen = (invitation: UserMembership) => {
    setModal({ invitation: invitation, isOpen: true });
  };

  const handleClose = () => {
    setModal((prevState) => {
      return {
        ...prevState,
        isOpen: false,
      };
    });
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  const handleManageMember = () => {
    if (selectedRow && selectedRow.id && selectedRow.orgId) {
      const userLink = generatePath(Routes.orgMemberDetails, {
        accountId: selectedRow.id,
        orgId: selectedRow.orgId,
      });
      history.push(userLink);
    }
    handleMenuClose();
  };

  const handleRemoveAction = () => {
    if (selectedRow) {
      handleClickOpen({
        organisationId: selectedRow.orgId,
        userMemberId: selectedRow.id,
        status: selectedRow.status,
        organisationName: selectedRow.orgName,
        userFullName: selectedRow.name,
        userEmail: selectedRow.email,
        organisationEmail: selectedRow.orgEmail,
        userFirstName: "",
        userLastName: "",
      });
    }
    handleMenuClose();
  };

  // Delete member mutation
  const deleteMemberMutation = useMutation({
      mutationFn: async (invitation: UserMembership) => {
        return deleteOrganisationMember(
          invitation.organisationId,
          invitation.userMemberId
        );
      },
      onSuccess: (_, invitation) => {
          // Update React Query cache - remove the deleted member from the list
          queryClient.setQueryData<UserMembership[]>(
            [QueryKeys.organisationInvitations, identityId],
            (oldData) => {
              if (!oldData) return [];
              return oldData.filter(
                (member) => member.userEmail !== invitation.userEmail
              );
            }
          );
          handleClose();
        },
    });

  const handleDelete = () => {
    deleteMemberMutation.mutate(modal.invitation);
  };

  const deletePermissionKeys = [PermissionsKeys.membersDelete];

  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const columns: GridColDef[] = [
    {
      flex: 1,
      field: "name",
      headerName: "Members",
      sortable: false,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <NameEmailAvatarRound
              name={params.value}
              variant={"circular"}
              isLoading={false}
              email={params.row.email}
              isAdmin={false}
            />
          </Box>
        );
      },
    },
    {
      width: 70,
      field: "role",
      headerName: "Role",
      headerAlign: "right",
      align: "right",
      sortable: false,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.role
          ? params.row.role.charAt(0).toUpperCase() + params.row.role.slice(1)
          : "Admin";
      },
    },
    {
      width: 90,
      field: "status",
      headerName: "Status",
      headerAlign: "right",
      align: "right",
      sortable: false,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
    },
    {
      width: 50,
      field: "menu",
      headerName: "",
      headerAlign: "right",
      align: "right",
      sortable: false,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={(event) => {
              setMenuAnchorEl(event.currentTarget);
              setSelectedRow(params.row);
            }}
          >
            <EllipsisVertical />
          </IconButton>
        </Box>
      ),
    },
  ];

  const developerNameOrEmail: string = modal.invitation.userFullName
    ? modal.invitation.userFullName
    : modal.invitation.userEmail;
  return (
    <Box marginY={2}>
      {deleteMemberMutation.isSuccess && (
        <Box marginBottom={1} pt={2}>
          <Alert variant="filled" severity="success">
            User has been deleted from the organisation
          </Alert>
        </Box>
      )}

      {deleteMemberMutation.isError && (
        <Box marginBottom={1} pt={2}>
          <Alert variant="filled" severity="error">
            {(deleteMemberMutation.error as Error)?.message ||
              "An error occurred while deleting the member"}
          </Alert>
        </Box>
      )}

      <ConfirmationModal
        isLoading={deleteMemberMutation.isPending}
        isError={deleteMemberMutation.isError}
        open={modal.isOpen}
        header={"Remove this member?"}
        body={`Are you sure you want to remove "${developerNameOrEmail}" from "${modal.invitation.organisationName}"? They will no longer be able to access this org unless invited again.`}
        leftButtonText={"CANCEL"}
        leftButtonColor="primary"
        rightButtonText={"REMOVE MEMBER"}
        rightButtonColor="error"
        handleClose={() => handleClose()}
        handleLeftButton={() => handleClose()}
        handleRightButton={() => handleDelete()}
      ></ConfirmationModal>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={handleManageMember}
          disabled={
            !selectedRow ||
            !selectedRow.id ||
            !selectedRow.orgId ||
            !selectedRow.name // If name is missing, email may not be linked to an account
          }
        >
          <ListItemIcon>
            <Settings size={20} />
          </ListItemIcon>
          <ListItemText>Manage Member</ListItemText>
        </MenuItem>

        <Divider sx={{ padding: "0px", margin: "0px !important" }} />

        <TooltipWrapper
          title={permissionDeniedMessageString(deletePermissionKeys)}
        >
          {(() => {
            const isDisabled =
              !selectedRow ||
              !user ||
              !isPermitted(deletePermissionKeys) ||
              selectedRow?.role === Role.OWNER;
            const isLeave =
              selectedRow && user && user.email === selectedRow.email;

            return (
              <MenuItem onClick={handleRemoveAction} disabled={isDisabled}>
                {isLeave ? (
                  <>
                    <ListItemIcon
                      sx={{ color: isDisabled ? undefined : "red" }}
                    >
                      <CircleX size={20} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ color: isDisabled ? undefined : "red" }}
                    >
                      Leave
                    </ListItemText>
                  </>
                ) : (
                  <>
                    <ListItemIcon
                      sx={{ color: isDisabled ? undefined : "orange" }}
                    >
                      <CircleMinus size={20} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ color: isDisabled ? undefined : "orange" }}
                    >
                      Remove
                    </ListItemText>
                  </>
                )}
              </MenuItem>
            );
          })()}
        </TooltipWrapper>
      </Menu>

      <DataGridPremium
        autoHeight={true}
        disableRowSelectionOnClick={true}
        hideFooterSelectedRowCount={false}
        rowHeight={72}
        rows={invitationsToRows(memberships)}
        columns={columns}
        pagination={true}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={false}
        loading={isLoading}
      />
    </Box>
  );
};

const invitationsToRows = (invitationList: UserMembership[]) => {
  const rows = invitationList
    .filter((i) => i.status === "Accepted" || i.status === "Pending")
    .sort((a: UserMembership, b: UserMembership) =>
      a.userFullName.localeCompare(b.userFullName)
    )
    .map((key) => {
      return {
        id: key.userMemberId,
        orgId: key.organisationId,
        orgName: key.organisationName,
        orgEmail: key.organisationEmail,
        members: key.userFullName,
        email: key.userEmail,
        name: key.userFullName,
        role: key.role,
        status: key.status == "Pending" ? "Invited" : key.status,
      };
    });
  return rows;
};
